import { api } from './configs/axiosConfig';
export const UserAPI = {
    /**
     * Get all users or a specific user by username
     */
    getUsers: async function (username) {
        const params = username ? { username, all: true } : { all: true };
        const response = await api.get('/user', { params });
        return response?.data;
    },
    /**
     * Create a new user
     */
    createUser: async function (userData) {
        const response = await api.post('/user', userData);
        return response?.data;
    },
    /**
     * Update an existing user
     */
    updateUser: async function (id, userData) {
        // Backend expects update via query parameter (?id=) not RESTful /user/:id path
        const response = await api.put(`/user`, userData, {
            params: { id }
        });
        return response.data;
    },
    /**
     * Update user preferences (name, phone, language, audio settings)
     * Convenience method that handles the payload construction for settings updates
     */
    async updateUserPreferences(user, preferences) {
        const existingDetails = user.details || {};
        const mergedMessagePreferences = {
            ...(existingDetails.messagePreferences || {}),
            ...(preferences.messagePreferences || {})
        };
        const details = {
            ...existingDetails,
            selected_language: preferences.selectedLanguage,
            audio: preferences.audio,
            messagePreferences: mergedMessagePreferences
        };
        // Spread userAttributes then remove phone — the cookie may be stale/empty.
        // Phone is controlled entirely via preferences.phone below.
        const userAttributes = {
            ...user.userAttributes,
            name: preferences.preferredName
        };
        delete userAttributes.phone;
        // undefined = don't change, null = clear (send ''), string = update
        if (preferences.phone !== undefined) {
            userAttributes.phone = preferences.phone ?? '';
        }
        const payload = {
            resourceType: 'User',
            username: user.username,
            userAttributes,
            details,
            metadata: user?.metadata
        };
        return this.updateUser(user.id, payload);
    },
    /**
     * Add feedback rating to user details
     * Convenience method that appends a new rating to the ratings array in user details
     */
    async addFeedbackRating(user, rating) {
        // Get existing ratings array or create new one
        const existingDetails = user.details || {};
        const existingRatings = Array.isArray(existingDetails.ratings)
            ? existingDetails.ratings
            : [];
        const payload = {
            resourceType: 'User',
            username: user.username,
            userAttributes: user.userAttributes,
            details: {
                ...existingDetails,
                ratings: [...existingRatings, rating]
            },
            metadata: user?.metadata
        };
        return this.updateUser(user.id, payload);
    },
    /**
     * Delete a user by ID or username
     */
    deleteUser: async function (id, username) {
        const params = {};
        if (id)
            params.id = id;
        if (username)
            params.username = username;
        const response = await api.delete('/user', { params });
        return response?.data;
    },
    /**
     * Assign a role to a user
     */
    assignRole: async function (username, roleData) {
        const response = await api.post(`/user/role?username=${username}`, roleData);
        return response?.data;
    },
    /**
     * Update a role assigned to a user
     */
    updateUserRole: async function (username, roleData) {
        const response = await api.put(`/user/role?username=${username}`, roleData);
        return response?.data;
    },
    /**
     * Remove a role from a user
     */
    removeRole: async function (userId, roleId) {
        const response = await api.delete(`/user/role?userId=${userId}&roleId=${roleId}`);
        return response?.data;
    },
    /**
     * Get all roles or roles for a specific user
     */
    getRoles: async function (username) {
        const params = username ? { username } : { all: true };
        const response = await api.get('/role', { params });
        return response?.data;
    },
    /**
     * Get roles specifically assigned to a user by username
     */
    getUserRoles: async function (username) {
        const response = await api.get('/user/role', {
            params: { username }
        });
        return response?.data;
    },
    /**
     * Get all users with their roles aggregated - optimized for Team Access page
     * This method returns all users with their assigned roles in a single API call
     * to avoid multiple individual getUserRoles calls
     */
    getUsersWithRoles: async function () {
        const response = await api.get('/user/with-roles');
        return response?.data;
    },
    /**
     * Get all roles with their policies and assignment counts aggregated - optimized for Role Management page
     * This method returns all roles with their policies and user assignment counts in a single API call
     * to avoid multiple individual getRolePolicies and getRoleAssignmentCount calls
     */
    getRolesWithPoliciesAndCounts: async function () {
        const response = await api.get('/role/with-policies-and-counts');
        return response?.data;
    },
    /**
     * Create a new role
     */
    createRole: async function (roleData) {
        const response = await api.post('/role', roleData);
        return response?.data;
    },
    /**
     * Update an existing role
     */
    updateRole: async function (id, roleData) {
        const response = await api.put(`/role/${id}`, roleData);
        return response?.data;
    },
    /**
     * Delete a role
     */
    deleteRole: async function (id) {
        const response = await api.delete(`/role/${id}`);
        return response?.data;
    },
    /**
     * Get all policies or policies for a specific user
     */
    getPolicies: async function (username) {
        const params = username ? { username } : { all: true };
        const response = await api.get('/policy', { params });
        return response?.data;
    },
    /**
     * Create a new policy
     */
    createPolicy: async function (policyData) {
        const response = await api.post('/policy', policyData);
        return response?.data;
    },
    /**
     * Update an existing policy
     */
    updatePolicy: async function (id, policyData) {
        const response = await api.put(`/policy/${id}`, policyData);
        return response?.data;
    },
    /**
     * Delete a policy
     */
    deletePolicy: async function (id) {
        const response = await api.delete(`/policy/${id}`);
        return response?.data;
    },
    /**
     * Get policies assigned to a specific role
     */
    getRolePolicies: async function (roleId) {
        const response = await api.get(`/rolepolicy/${roleId}/policy`);
        return response?.data;
    },
    /**
     * Get rolepolicy records for a specific policy (to check which roles use it)
     */
    getPolicyRoleRecords: async function (policyId) {
        const response = await api.get(`/rolepolicy/policy/${policyId}/records`);
        return response?.data?.resource || [];
    },
    /**
     * Delete a specific rolepolicy by its ID
     */
    deleteRolePolicyById: async function (rolePolicyId) {
        await api.delete(`/rolepolicy/${rolePolicyId}`);
    },
    /**
     * Get the number of users assigned to a role from principalrole table
     */
    getRoleAssignmentCount: async function (roleId) {
        const response = await api.get(`/role/${roleId}/assignment/count`);
        return response?.data?.count || 0;
    },
    /**
     * Create role-policy associations (assign policies to a role)
     */
    createRolePolicy: async function (requestData) {
        const response = await api.post('/rolepolicy', requestData);
        return response?.data;
    },
    /**
     * Assign a policy to a role (legacy method - kept for backward compatibility)
     */
    assignPolicyToRole: async function (roleId, policyId) {
        const response = await api.post(`/role/${roleId}/policy/${policyId}`);
        return response?.data;
    },
    /**
     * Remove a policy from a role (legacy method - kept for backward compatibility)
     */
    removePolicyFromRole: async function (roleId, policyId) {
        const response = await api.delete(`/role/${roleId}/policy/${policyId}`);
        return response?.data;
    },
    /**
     * Remove all principal (user/group) assignments for a specific role
     */
    deleteAllRoleAssignments: async function (roleId) {
        const response = await api.get(`/role/${roleId}/assignment`);
        const assignments = response?.data?.resource || [];
        if (assignments.length > 0) {
            const removeList = assignments.map((a) => ({
                principal_id: parseInt(a.entity_id),
                principal_type: a.principal_type
            }));
            await api.post(`/role/${roleId}/assignment`, { remove: removeList });
        }
    },
    /**
     * Delete all role-policy mappings for a specific role
     */
    deleteAllRolePolicies: async function (roleId) {
        // Get all policies for this role (which includes rolepolicy_id)
        const rolePoliciesResponse = await UserAPI.getRolePolicies(roleId);
        const rolePolicies = rolePoliciesResponse.resource;
        // Delete each rolepolicy record using the proper /rolepolicy/:rolepolicy_id endpoint
        for (const policy of rolePolicies) {
            await UserAPI.deleteRolePolicyById(policy.rolepolicy_id);
        }
    },
    /**
     * Remove specific policies from a role using the proper rolepolicy deletion API
     */
    removeSpecificPoliciesFromRole: async function (roleId, policyIds) {
        // Get all policies for this role (which includes rolepolicy_id)
        const rolePoliciesResponse = await UserAPI.getRolePolicies(roleId);
        const rolePolicies = rolePoliciesResponse.resource;
        // Find the rolepolicy IDs for the policies we want to remove
        const rolePolicyIdsToDelete = rolePolicies
            .filter((policy) => policyIds.includes(Number(policy.id)))
            .map((policy) => policy.rolepolicy_id);
        // Delete each rolepolicy record using the proper /rolepolicy/:rolepolicy_id endpoint
        for (const rolePolicyId of rolePolicyIdsToDelete) {
            await UserAPI.deleteRolePolicyById(rolePolicyId);
        }
    },
    /**
     * Get all applications
     */
    getAllApplications: async function (orgId) {
        const params = { all: true, ...(orgId ? { orgId } : {}) };
        const response = await api.get('/application', { params });
        return response?.data;
    },
    /**
     * Get  applications
     */
    getApplications: async function (orgId) {
        const params = orgId ? { orgId } : {};
        const response = await api.get('/application', { params });
        return response?.data;
    },
    /**
     * Get application by ID
     */
    getApplicationById: async function (applicationId) {
        const response = await api.get(`/application/${applicationId}`);
        return response?.data;
    },
    /**
     * Resend registration email for external users
     */
    resendRegistrationEmail: async function (userId) {
        const response = await api.post(`/user/${userId}/invite/resend`, {
            userId
        });
        return response?.data;
    },
    /**
     * Reset password for external users
     */
    resetPassword: async function (userId) {
        const response = await api.post(`/user/${userId}/passwordreset/initiate`);
        return response?.data;
    },
    /**
     * Validate email address using UserLookUp API via backend
     */
    validateEmail: async function (email) {
        const response = await api.get('/user/userlookup', {
            params: { email }
        });
        // The backend now returns a structured response with resourceType
        const data = response?.data;
        return {
            isValid: data?.isValid,
            userInfo: data?.userInfo,
            error: data?.error
        };
    },
    /**
     * Create a new application
     */
    createApplication: async function (applicationData) {
        const response = await api.post('/application', applicationData);
        return response?.data;
    },
    /**
     * Update an application
     */
    updateApplication: async function (applicationId, applicationData) {
        const response = await api.put(`/application/${applicationId}`, applicationData);
        return response?.data;
    },
    /**
     * Delete an application
     */
    deleteApplication: async function (applicationId) {
        const response = await api.delete(`/application/${applicationId}`);
        return response?.data;
    },
    /**
     * Get all user groups or user groups for a specific organization
     */
    getUserGroups: async function (organizationId) {
        const params = organizationId
            ? { organization_id: organizationId, all: true }
            : { all: true };
        const response = await api.get('/usergroup', { params });
        return response?.data;
    },
    /**
     * Get a specific user group by ID
     */
    getUserGroupById: async function (id) {
        const response = await api.get(`/usergroup/${id}`);
        return response?.data;
    },
    /**
     * Create a new user group
     */
    createUserGroup: async function (groupData) {
        const response = await api.post('/usergroup', groupData);
        return response?.data;
    },
    /**
     * Update an existing user group
     */
    updateUserGroup: async function (id, groupData) {
        const response = await api.put(`/usergroup/${id}`, groupData);
        return response?.data;
    },
    /**
     * Delete a user group
     */
    deleteUserGroup: async function (id) {
        const response = await api.delete(`/usergroup/${id}`);
        return response?.data;
    },
    /**
     * Get members of a user group
     */
    getUserGroupMembers: async function (groupId) {
        const response = await api.get(`/usergroup/${groupId}/members`);
        return response?.data;
    },
    /**
     * Add users to a user group (bulk assignment)
     */
    addUsersToGroup: async function (groupId, userIds) {
        const response = await api.post(`/usergroup/${groupId}/assignment/bulk`, {
            add: userIds,
            remove: []
        });
        return response?.data;
    },
    /**
     * Remove multiple users from a user group (bulk removal)
     */
    removeUsersFromGroup: async function (groupId, userIds) {
        // Assuming backend supports bulk removal via POST to /usergroup/:groupId/assignment/bulk
        const response = await api.post(`/usergroup/${groupId}/assignment/bulk`, {
            add: [],
            remove: userIds
        });
        return response?.data;
    },
    /**
     * Assign roles to a principal (user or user group)
     * principal_type: 1 = User, 2 = Group
     */
    assignRolesToPrincipal: async function (roleIds, principalId, principalType) {
        const results = [];
        for (const roleId of roleIds) {
            const response = await api.post(`/role/${roleId}/assignment`, {
                assign: [
                    {
                        principal_id: principalId,
                        principal_type: principalType
                    }
                ]
            });
            results.push(response?.data);
        }
        return results;
    },
    /**
     * Remove roles from a principal (user or user group)
     * principal_type: 1 = User, 2 = Group
     */
    removeRolesFromPrincipal: async function (roleIds, principalId, principalType) {
        const results = [];
        for (const roleId of roleIds) {
            const response = await api.post(`/role/${roleId}/assignment`, {
                remove: [
                    {
                        principal_id: principalId,
                        principal_type: principalType
                    }
                ]
            });
            results.push(response?.data);
        }
        return results;
    }
};
